import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Calendar, Phone, Mail, Sparkles, MessageCircle, X, ChevronUp } from 'lucide-react';
const PHONE = '07061032706';
const PHONE_INTL = '2347061032706';
type DockItem = {
  id: string;
  label: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  bg: string;
  iconColor: string;
  action: () => void;
};
export function FloatingSearch() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const onScroll = () => setHasScrolled(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  // Cycle: visible 40s → hidden (collapsed) 30s → repeat
  useEffect(() => {
    if (!hasScrolled) return;
    let hideTimer: ReturnType<typeof setTimeout>;
    let showTimer: ReturnType<typeof setTimeout>;
    const cycleShow = () => {
      setIsVisible(true);
      hideTimer = setTimeout(() => {
        setIsVisible(false);
        setContactOpen(false);
        showTimer = setTimeout(cycleShow, 30000);
      }, 40000);
    };
    const initial = setTimeout(cycleShow, 2000);
    return () => {
      clearTimeout(initial);
      clearTimeout(hideTimer);
      clearTimeout(showTimer);
    };
  }, [hasScrolled]);
  const scrollTo = (selector: string) => {
    const id = selector.replace('#', '');
    if (location.pathname !== '/') {
      navigate('/#' + id);
      return;
    }
    const el = document.getElementById(id);
    el?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const items: DockItem[] = [{
    id: 'estates',
    label: 'Estates',
    icon: Home,
    bg: 'bg-gold',
    iconColor: 'text-ink',
    action: () => scrollTo('#properties')
  }, {
    id: 'inspection',
    label: 'Inspection',
    icon: Calendar,
    bg: 'bg-ink',
    iconColor: 'text-white',
    action: () => scrollTo('#book')
  }, {
    id: 'assistant',
    label: 'Assistant',
    icon: Sparkles,
    bg: 'bg-gradient-to-br from-gold to-gold-dark',
    iconColor: 'text-ink',
    action: () => window.dispatchEvent(new CustomEvent('open-zee-chat'))
  }, {
    id: 'email',
    label: 'Email',
    icon: Mail,
    bg: 'bg-blue-600',
    iconColor: 'text-white',
    action: () => {
      window.location.href = 'mailto:info@zylushomes.com';
    }
  }, {
    id: 'contact',
    label: 'Contact',
    icon: Phone,
    bg: 'bg-emerald-500',
    iconColor: 'text-white',
    action: () => setContactOpen(v => !v)
  }, {
    id: 'whatsapp',
    label: 'WhatsApp',
    icon: MessageCircle,
    bg: 'bg-green-500',
    iconColor: 'text-white',
    action: () => {
      window.location.href = `https://wa.me/${PHONE_INTL}?text=${encodeURIComponent('Hello Zylus Homes, I would like to make an enquiry.')}`;
    }
  }];
  const handleClick = (item: DockItem) => {
    if (item.id === 'contact') {
      item.action();
      return;
    }
    item.action();
    if (item.id !== 'assistant') {
      setContactOpen(false);
      setIsVisible(false);
    }
  };
  const collapse = () => {
    setIsVisible(false);
    setContactOpen(false);
  };
  const expand = () => setIsVisible(true);
  return <>
      {/* Centered Contact Modal — brand colors, fully responsive */}
      <AnimatePresence data-id="element-220">
        {contactOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setContactOpen(false)} className="fixed inset-0 z-[60] bg-ink/70 backdrop-blur-md flex items-center justify-center p-4" data-id="element-221">
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.92
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 30,
          scale: 0.92
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 26
        }} onClick={e => e.stopPropagation()} className="relative w-full max-w-sm bg-bone rounded-3xl shadow-2xl border border-gold/30 overflow-hidden" data-id="element-222">
              {/* Decorative gold orb */}
              <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gold/30 blur-3xl pointer-events-none" data-id="element-223" />
              <div className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full bg-ink/5 blur-3xl pointer-events-none" data-id="element-224" />

              {/* Header band */}
              <div className="relative bg-ink text-white px-6 pt-6 pb-8" data-id="element-225">
                <button onClick={() => setContactOpen(false)} aria-label="Close contact" className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-gold hover:text-ink text-white flex items-center justify-center transition-colors" data-id="element-226">
                  <X className="w-4 h-4" data-id="element-227" />
                </button>
                <div className="flex items-center gap-3 mb-3" data-id="element-228">
                  <div className="w-10 h-10 rounded-full bg-gold flex items-center justify-center shrink-0" data-id="element-229">
                    <Phone className="w-5 h-5 text-ink" strokeWidth={2.4} data-id="element-230" />
                  </div>
                  <div data-id="element-231">
                    <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold" data-id="element-232">
                      Speak with us
                    </div>
                    <div className="font-serif text-xl leading-tight" data-id="element-233">
                      Call Zylus directly
                    </div>
                  </div>
                </div>
                <a href={`tel:${PHONE}`} className="block font-mono text-2xl tracking-wider text-gold hover:text-white transition-colors" data-id="element-234">
                  {PHONE}
                </a>
              </div>

              {/* Action buttons */}
              <div className="relative px-6 py-6 space-y-3" data-id="element-235">
                <motion.a whileHover={{
              y: -2
            }} whileTap={{
              scale: 0.97
            }} href={`tel:${PHONE}`} className="flex items-center justify-center gap-2.5 w-full bg-gold hover:bg-gold-dark text-ink text-xs font-bold uppercase tracking-widest py-4 rounded-xl transition-colors shadow-lg shadow-gold/20" data-id="element-236">
                  <Phone className="w-4 h-4" data-id="element-237" />
                  Call Now
                </motion.a>
                <motion.a whileHover={{
              y: -2
            }} whileTap={{
              scale: 0.97
            }} href={`https://wa.me/${PHONE_INTL}?text=${encodeURIComponent('Hello Zylus Homes, I would like to make an enquiry.')}`} className="flex items-center justify-center gap-2.5 w-full bg-ink hover:bg-ink/90 text-white text-xs font-bold uppercase tracking-widest py-4 rounded-xl transition-colors border border-ink" data-id="element-238">
                  <MessageCircle className="w-4 h-4 text-gold" data-id="element-239" />
                  Chat on WhatsApp
                </motion.a>

                <p className="text-[10px] text-ink/50 pt-2 text-center leading-relaxed" data-id="element-240">
                  Lines open Mon – Sat · 9am – 6pm WAT
                </p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Collapsed side tab — visible whenever dock is hidden but user has scrolled */}
      <AnimatePresence data-id="element-241">
        {hasScrolled && !isVisible && <motion.button key="dock-tab" onClick={expand} initial={{
        x: 60,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 60,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 24
      }} whileHover={{
        scale: 1.08,
        x: -4
      }} whileTap={{
        scale: 0.95
      }} aria-label="Open quick actions" className="fixed right-3 bottom-24 z-40 flex flex-col items-center gap-1 bg-ink text-white pl-2 pr-2 py-3 rounded-l-2xl shadow-[0_8px_30px_rgba(0,0,0,0.25)] border border-white/10" data-id="element-242">
            <ChevronUp className="w-4 h-4 text-gold" strokeWidth={2.5} data-id="element-243" />
            <Sparkles className="w-5 h-5 text-gold" fill="currentColor" data-id="element-244" />
            <span className="text-[8px] font-bold uppercase tracking-widest text-white/70 [writing-mode:vertical-rl] mt-1" data-id="element-245">
              Open
            </span>
            {/* pulse ring */}
            <motion.span aria-hidden animate={{
          scale: [1, 1.6, 1],
          opacity: [0.5, 0, 0.5]
        }} transition={{
          duration: 2.4,
          repeat: Infinity
        }} className="absolute inset-0 rounded-l-2xl border border-gold pointer-events-none" data-id="element-246" />
          </motion.button>}
      </AnimatePresence>

      {/* Dock */}
      <AnimatePresence data-id="element-247">
        {isVisible && <motion.div initial={{
        y: 80,
        opacity: 0,
        scale: 0.8
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 80,
        opacity: 0,
        scale: 0.85
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 26
      }} style={{
        left: '50%',
        x: '-50%'
      }} className="fixed bottom-5 z-40" data-id="element-248">
            {/* Contact popover */}
            <AnimatePresence data-id="element-249">
              {contactOpen && <motion.div initial={{
            opacity: 0,
            y: 12,
            scale: 0.92
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            y: 12,
            scale: 0.92
          }} transition={{
            type: 'spring',
            stiffness: 320,
            damping: 24
          }} className="absolute bottom-full mb-3 left-1/2 -translate-x-1/2 w-[min(92vw,340px)] bg-white rounded-2xl shadow-2xl border border-ink/10 p-5 overflow-hidden" data-id="element-250">
                  <button onClick={() => setContactOpen(false)} aria-label="Close contact" className="absolute top-3 right-3 w-7 h-7 rounded-full bg-bone hover:bg-ink hover:text-white text-ink/60 flex items-center justify-center transition-colors" data-id="element-251">
                    <X className="w-3.5 h-3.5" data-id="element-252" />
                  </button>

                  <div className="text-[10px] font-bold tracking-[0.25em] uppercase text-gold-dark mb-2" data-id="element-253">
                    Speak with us
                  </div>
                  <div className="font-serif text-2xl text-ink leading-tight mb-1" data-id="element-254">
                    Call Zylus directly
                  </div>
                  <a href={`tel:${PHONE}`} className="text-ink/70 text-sm font-mono tracking-wide hover:text-gold-dark transition-colors" data-id="element-255">
                    {PHONE}
                  </a>

                  <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-256">
                    <motion.a whileHover={{
                y: -2
              }} whileTap={{
                scale: 0.96
              }} href={`tel:${PHONE}`} className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold uppercase tracking-widest py-3 rounded-xl transition-colors" data-id="element-257">
                      <Phone className="w-3.5 h-3.5" data-id="element-258" /> Call Now
                    </motion.a>
                    <motion.a whileHover={{
                y: -2
              }} whileTap={{
                scale: 0.96
              }} href={`https://wa.me/${PHONE_INTL}`} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white text-xs font-bold uppercase tracking-widest py-3 rounded-xl transition-colors" data-id="element-259">
                      <MessageCircle className="w-3.5 h-3.5" data-id="element-260" /> WhatsApp
                    </motion.a>
                  </div>

                  <p className="text-[10px] text-ink/40 mt-3 leading-relaxed" data-id="element-261">
                    Lines open Mon–Sat · 9am – 6pm WAT
                  </p>
                </motion.div>}
            </AnimatePresence>

            <div className="relative flex items-center gap-1 bg-white/80 backdrop-blur-2xl border border-ink/10 rounded-2xl px-2 py-2 shadow-[0_12px_40px_rgba(0,0,0,0.18)]" data-id="element-262">
              {/* Collapse button (replaces close) */}
              <button onClick={collapse} aria-label="Collapse dock" className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-ink text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md z-10" data-id="element-263">
                <X className="w-3 h-3" strokeWidth={2.5} data-id="element-264" />
              </button>

              {items.map((item, i) => {
            const Icon = item.icon;
            const isActive = item.id === 'contact' && contactOpen;
            return <motion.button key={item.id} initial={{
              y: 30,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.05 + i * 0.05,
              type: 'spring',
              stiffness: 380,
              damping: 22
            }} whileHover={{
              y: -6,
              scale: 1.08
            }} whileTap={{
              scale: 0.92
            }} onClick={() => handleClick(item)} className="group flex flex-col items-center gap-1 px-1.5 py-1" data-id="element-265">
                    <div className={`${item.bg} w-10 h-10 sm:w-11 sm:h-11 rounded-xl flex items-center justify-center shadow-md ${isActive ? 'ring-2 ring-gold ring-offset-2 ring-offset-white/80' : ''}`} data-id="element-266">
                      <Icon className={`w-[18px] h-[18px] sm:w-5 sm:h-5 ${item.iconColor}`} strokeWidth={2.2} data-id="element-267" />
                    </div>
                    <span className="text-[9px] font-semibold text-ink/70 group-hover:text-ink transition-colors" data-id="element-268">
                      {item.label}
                    </span>
                  </motion.button>;
          })}
            </div>

            <div className="absolute inset-0 -z-10 rounded-2xl bg-gold/15 blur-2xl pointer-events-none" data-id="element-269" />
          </motion.div>}
      </AnimatePresence>
    </>;
}